import { sharedHeaders } from "../utils/constants"

const createDraft = {
  "type": "endpoint",
  "method": "post",
  "title": "Create Draft",
  "path": "/v1/draft",
  "description": "Create a purchase draft with the provided details",
  "request": {
    "headers": [
      ...sharedHeaders
    ],
    "body": {
      "content": {
        "application/json": {
          "schema": {
            "$ref": "#/components/schemas/DraftRequestBody"
          }
        }
      }
    }
  },
  "responses": {
    "200": {
      "description": "Successful response",
      "content": {
        "application/json": {
          "schema": {
            "type": "object",
            "properties": {
              "draft_id": {
                "type": "string",
                "description": "ID of the created draft purchase",
                "example": "lX8RkQmK49iPVWRt9gdc"
              },
              "product_name": {
                "type": "string",
                "description": "Name of the product being purchased",
                "example": "ASSURGLISSE"
              },
              "price": {
                "type": "object",
                "description": "Total price of the draft purchase",
                "example": 16.2
              },
              "price_currency": {
                "type": "string",
                "description": "Currency of the price",
                "example": "EUR"
              },
              "documents": {
                "type": "object",
                "properties": {
                  "cg": {
                    "type": "string",
                    "description": "URL to the terms and conditions document",
                    "example": "https://example.com/terms"
                  },
                  "ipid": {
                    "type": "string",
                    "description": "URL to the insurance product information document",
                    "example": "https://example.com/ipid"
                  }
                },
                "required": [
                  "cg",
                  "ipid"
                ]
              },
              "guarantees": {
                "type": "array",
                "items": {
                  "type": "object",
                  "properties": {
                    "title": {
                      "type": "string",
                      "description": "Localized title of the guarantees section",
                      "example": "Guarantee 1. Costs for search and rescue and initial medical transport, with no advance for expenses"
                    },
                    "text": {
                      "type": "string",
                      "description": "Localized text describing the guarantees",
                      "example": "- up to actual costs if the accident occurs in France\\n- and up to a maximum of ..."
                    }
                  },
                  "required": [
                    "title",
                    "text"
                  ]
                }
              },
              "exclusions": {
                "type": "object",
                "properties": {
                  "title": {
                    "type": "string",
                    "description": "Localized title of the exclusions section",
                    "example": "MAIN EXCLUSIONS TO INSURANCE COVER"
                  },
                  "text": {
                    "type": "string",
                    "description": "Localized text describing the exclusions",
                    "example": "We strive to ensure that our insurance policies are as comprehensive as possible, but there are a few exclusions that apply ..."
                  }
                },
                "required": [
                  "title",
                  "text"
                ]
              },
              "hosted_payment_url": {
                "type": "string",
                "description": "URL to the hosted payment gate (if hosted payment was requested)",
                "example": "https://secure.payplug.com/pay/123"
              }
            },
            "required": [
              "draft_id",
              "product_name",
              "price",
              "price_currency",
              "documents",
              "guarantees",
              "exclusions"
            ]
          }
        }
      }
    },
    "400": {
      "description": "Bad Request"
    },
    "401": {
      "description": "Unauthorized"
    },
    "500": {
      "description": "Internal Server Error"
    }
  },
  "components": {
    "schemas": {
      "InsuredPerson": {
        "type": "object",
        "properties": {
          "firstname": {
            "type": "string",
            "description": "First name of the insured person",
            "example": "Jane"
          },
          "lastname": {
            "type": "string",
            "description": "Last name of the insured person",
            "example": "Doe"
          }
        },
        "required": [
          "firstname",
          "lastname"
        ]
      },
      "InsuredDate": {
        "type": "string",
        "description": "Date of the insured activity in DD/MM/YYYY format",
        "example": "25/12/2023"
      },
      "DraftRequestBody": {
        "type": "object",
        "properties": {
          "product_id": {
            "type": "string",
            "description": "ID of the product being purchased",
            "example": "rMYNCOjLzlwABlFLY5Cq"
          },
          "policyholder": {
            "type": "object",
            "properties": {
              "firstname": {
                "type": "string",
                "description": "First name of the policyholder",
                "example": "John"
              },
              "lastname": {
                "type": "string",
                "description": "Last name of the policyholder",
                "example": "Doe"
              },
              "email": {
                "type": "string",
                "description": "Email address of the policyholder",
                "example": "john.doe@example.com"
              },
              "company_name": {
                "type": "string",
                "description": "Company name (if applicable)",
                "example": "Example Corp"
              }
            },
            "required": [
              "firstname",
              "lastname",
              "email"
            ]
          },
          "insured_persons": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/InsuredPerson"
            },
            "description": "List of insured persons"
          },
          "dates": {
            "type": "array",
            "items": {
              "$ref": "#/components/schemas/InsuredDate",
              "description": "Date of the insured activity in DD/MM/YYYY format"
            },
            "description": "Selected dates for the activity (required if product uses date-based pricing - duration dates_required is true)"
          },
          "terms_accepted": {
            "type": "boolean",
            "description": "Whether the terms and conditions have been accepted",
            "example": true
          },
          "proof_terms_accepted": {
            "type": "boolean",
            "description": "Whether the proof of terms acceptance has been provided",
            "example": true
          },
          "hosted_payment": {
            "type": "boolean",
            "description": "Whether hosted payment is requested. If set to true, Payplug payment request will be created and url to payment gate returned in response. Drafts with hosted payment cannot be manually completed or cancelled via API",
            "example": true
          },
          "hosted_payment_return_url": {
            "type": "string",
            "description": "URL to which the user is redirected from Payplug payment gate after hosted payment completion. Required if hosted_payment is true",
            "example": "https://example.com/return"
          },
          "hosted_payment_cancel_url": {
            "type": "string",
            "description": "URL to which the user is redirected from Payplug payment gate after hosted payment cancellation. Required if hosted_payment is true",
            "example": "https://example.com/cancel"
          },
          "notification_url": {
            "type": "string",
            "description": "Webhook URL to which payment status change notifications are sent. Optional, payment status can be monitored via status polling endpoint. See <a href=/webhooks>Webhooks</a>",
            "example": "https://api.example.com/notify"
          },
          "user_id": {
            "type": "string",
            "description": "ID of the user in your system (for tracking purposes, if applicable)",
            "example": "user-123"
          }
        },
        "required": [
          "product_id",
          "policyholder",
          "insured_persons",
          "terms_accepted",
          "proof_terms_accepted"
        ]
      }
    }
  }
}

export { createDraft }
